import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { STORAGE_KEYS } from '@/config';

import { updateMyLocale } from '@/lib/data/provider';

import { applyDirection, isRTLLocale, tryReload } from './direction';
import { i18n, persistLocale, type Locale } from './index';
import type { NumeralOptions } from './format';

interface LocaleContextValue {
  locale: Locale;
  isRTL: boolean;
  arabicNumerals: boolean;
  numeralOptions: NumeralOptions;
  /** Returns true when the app needs a manual restart to finish applying RTL. */
  setLocale: (locale: Locale) => Promise<boolean>;
  setArabicNumerals: (value: boolean) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  initialLocale,
  children,
}: {
  initialLocale: Locale;
  children: React.ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const [arabicNumerals, setArabicNumeralsState] = useState(false);

  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEYS.arabicNumerals)
      .then((stored) => {
        if (stored === 'true') setArabicNumeralsState(true);
      })
      .catch(() => {
        // Preference simply stays at its default when storage is unavailable.
      });
  }, []);

  // Sync the ACTIVE UI language to the server on every launch — not just when the
  // user manually switches. Without this, an English-by-default user who never
  // tapped the language toggle kept a stale profiles.locale and received Arabic
  // push notifications. setLocale() covers later changes; this covers the initial
  // load. Best-effort: no-ops until the auth session is restored (getUser()).
  useEffect(() => {
    void updateMyLocale(locale);
    // Run once for the launch locale; subsequent changes go through setLocale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = useCallback(async (next: Locale): Promise<boolean> => {
    await persistLocale(next);
    await i18n.changeLanguage(next);
    setLocaleState(next);
    // Mirror the chosen language to the server so push notifications match the
    // UI language (pushes read profiles.locale). Best-effort; the launch sync is
    // the mount effect above, this covers every manual change.
    void updateMyLocale(next);
    const needsReload = applyDirection(next);
    if (needsReload) {
      const reloaded = await tryReload();
      return !reloaded;
    }
    return false;
  }, []);

  const setArabicNumerals = useCallback((value: boolean) => {
    setArabicNumeralsState(value);
    AsyncStorage.setItem(STORAGE_KEYS.arabicNumerals, String(value)).catch(() => {
      // Non-fatal.
    });
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      isRTL: isRTLLocale(locale),
      arabicNumerals,
      numeralOptions: { locale, arabicNumerals },
      setLocale,
      setArabicNumerals,
    }),
    [locale, arabicNumerals, setLocale, setArabicNumerals],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used inside LocaleProvider');
  }
  return context;
}
